import Header from "../components/Header";
import CaptionedImage from "../components/CaptionedImage";
import Footer from "../components/Footer";
import ScrollToTopButton from "../components/ScrollToTopButton";

const PaperTradeMERN: React.FC = () => {
  return (
    <div>
      <Header />
      <ScrollToTopButton />
      <div className="flex justify-center break-words p-3">
        <div className="projectDetailsContainer w-full sm:w-10/12 xl:w-8/12">
          <h1 className="text-center">Paper Trade</h1>
          <h2 className="text-center">
            TLDR: I made every mistake possible but learned a lot in the
            process.
          </h2>
          <div className="flex items-center gap-3">
            <div className="my-5">
              <h1>Initial Intention</h1>
              <div className="gap-8 xl:flex">
                <div className="my-3 md:w-10/12 xl:w-6/12">
                  <CaptionedImage
                    src={"/res/papertradeweb/initialmock.png"}
                    id={"initialmock"}
                    caption={"An initial visual mockup of PaperTrade"}
                  />
                </div>
                <div className="my-3 xl:w-6/12">
                  <p className="mb-3">
                    Prior to this project, I had quite literally zero knowledge
                    of web development. The only web development experience that
                    I had was in the Beatshuffler project I had made using
                    Python and Django, which involved extremely basic HTML,
                    almost no CSS, and smidgeons of Javascript. Moreover, I
                    found web development in Python and Django extremely
                    unintuitive and so after some googling I was left with the
                    consensus that Javascript was more appropriate, and in
                    particular the use of a framework such as React, of which I
                    was also absolutely clueless about.
                  </p>
                  <p>
                    The goal of this project was simply to gain a better
                    understanding of full stack web development, which includes
                    a backend (even though I didn't even know what a backend
                    really was). Consequently, I thought I should simply try and
                    create a paper trading stock application as it is something
                    that I had done in previous projects. This entailed allowing
                    a user to have some amount of fictional money, and be able
                    to search for actual stocks and buy and sell them according
                    to their actual prices. To get these prices, I decided to
                    use the IEXCloud API, because I had used it in the past and
                    their Sandbox mode allowed me to make many API calls free of
                    cost [This mode has now been deprecated].
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="my-5">
            <div>
              <h1>Development Process</h1>
              <div className="my-5 gap-8 xl:flex">
                <div className="my-3 md:w-10/12 xl:w-6/12">
                  <CaptionedImage
                    src={"/res/papertradeweb/pureJS.png"}
                    id={"pureJS"}
                    caption={
                      "HTML, CSS, Vanilla Javascript Implementation of PaperTrade"
                    }
                  />
                </div>
                <div className="my-3 xl:w-6/12">
                  <h2>First Attempt - Vanilla Javascript Frontend</h2>
                  <p>
                    With only the vague idea that I would eventually need
                    something called React and whatever a backend was, I simply
                    opened up SublimeText and created an index.html file and
                    began assembling what I could using my basic HTML, CSS, and
                    Javascript stolen from StackOverflow. This first prototype
                    (image on the left) was capable of making API calls to fetch
                    a stock price, and taking user input to purchase a specified
                    quantity which would then be displayed in a table.
                    <br></br>
                    <br></br>
                    There were multiple issues with this. Firstly, I learnt that
                    coding in pure Javascript isn't especially pleasant due to
                    all of the document.getElementByID() calls and
                    eventListeners and general unintuitiveness. Secondly, I
                    still did not have a backend and so any stocks purchased
                    would simply disappear upon refresh. I had no clue how to
                    even begin creating a backend but I at least had the idea
                    that coding in React is probably a better experience than
                    pure Javascript—and so that's what I did.
                  </p>
                </div>
              </div>
              <h2>Second Attempt - React Frontend</h2>
              <div className="my-5 ">
                <p className="mb-3">
                  As React was completely new to me, I underwent the tutorial
                  which involved creating a game of Tic-Tac-Toe
                  (https://reactjs.org/tutorial/tutorial.html). I found this
                  tutorial really helpful and I began to translate my existing
                  project into React using Typescript.
                </p>
                <p className="mb-3">
                  Whilst I enjoyed React a lot more than vanilla Javascript, I
                  still found certain things unintuitive. In particular, it was
                  the fact that functions such as setState and useState hooks
                  are asynchronous that led many of my child components to have
                  different state values relative to the parent, or vice versa.
                  I eventually figured out that I could solve this using
                  useEffect hooks and careful handling of Javascript Promises
                  and Promise.then() syntax. Although I found async functions to
                  be slightly unintuitive, React was still provided a much
                  better developer experience relative to vanilla Javascript.
                </p>
                <p className="mb-3">
                  Eventually, I had recreated in Typescript and React what I had
                  originally made in vanilla Javascript. At this point, the
                  frontend of the website appeared complete to me.
                  Unfortunately, I quickly realized that many changes would need
                  to be made as I began developing the backend.
                </p>
              </div>
              <h2>Backend Development - MongoDB, Express.JS & Node.js </h2>
              <CaptionedImage
                src={"/res/papertradeweb/stockSearchAndTrending.png"}
                id={"stockSearchAndTrendingImg"}
                caption={
                  "The Stock Search Form and Trending Ten Stocks List (the most volatile stocks according to the IEXCloud API)"
                }
              />
              <p className="mb-3">
                Once again, I consulted Google for advice on what a backend even
                was and which one I should choose. Being somewhat intimidated by
                backends, I decided to opt for a NoSQL database as I did not
                want to get lost learning an entire new language (in hindsight,
                I now understand that SQL isn't too complicated, but I probably
                still made a good decision), which led me to choose MongoDB. To
                manage the server, I chose to use Node.js and Express.js,
                primarily because it seemed like there were many resources
                online that I could use to troubleshoot development.
              </p>
              <p className="mb-3">
                In particular, throughout the backend development process I
                heavily relied on a MERN stack tutorial created by MongoDB
                (https://www.mongodb.com/languages/mern-stack-tutorial). I
                modified the code from the tutorial to store data relevant to my
                purposes; a user's available cash, the stocks they owned, the
                quantity they owned, and more. Without this tutorial, I would
                have been pretty lost, even after having completed this, I am
                still not particularly confident in backend development, and so
                it is certainly something that I want to spend more time on in
                the future.
              </p>
              <div className="my-5">
                <h2>Putting it all together - MERN</h2>
                <CaptionedImage
                  src={"/res/papertradeweb/dashboard.png"}
                  id={"paperTradeDashboardImg"}
                  caption={"The PaperTrade Dashboard"}
                />
                <p>
                  After having assembled the backend, I was succesfully able to
                  make API calls from the frontend, allowing my website to
                  finally store data across sessions. However, it was at this
                  point that I found myself needing to make changes to my
                  frontend. Because I had initially created the frontend with
                  zero idea about how the backend would come into play, much of
                  the code regarding buying and selling stocks that I had
                  written did not easily interface with the backend.
                </p>
                <p>
                  For example, I had initially stored a user's stocks and cash
                  data completely on the frontend as React state variables, with
                  no reference to a database. Consequently, when a user bought
                  or sold a stock, data was only updated on the client side, the
                  frontend managed all of the logic; checking if the user has
                  sufficient cash, or whether they owned the stock they were
                  trying to sell. However, upon introducing a backend, I was now
                  encountering issues where users buying stocks that they
                  already owned created multiple rows in their portfolio table,
                  when there should instead be only one row but with the
                  quantity originally owned added to the quantity just bought.
                  Because of this, I ended up needing to modify lots of existing
                  frontend code to ensure that the client side state was
                  appropriately in sync with the backend, such that the content
                  displayed to the user was accurate to the user's data in the
                  database.
                </p>
                <p>
                  Nonetheless, I eventually managed to remedy these issues and
                  had a functional website which allowed the user to buy and
                  sell stocks, across sessions. My task now was to create a
                  login system to allow this for multiple users.
                </p>
              </div>
              <div>
                <h2>Login System </h2>
                <div className="">
                  <CaptionedImage
                    src={"/res/papertradeweb/loginCropped.png"}
                    id={"loginCroppedImg"}
                    caption={"Login page"}
                  />
                  <p>
                    In order to implement the login system, I heavily relied on
                    another tutorial created by the YouTube channel Traversy
                    Media
                    (https://www.youtube.com/watch?v=USaB1adUHM0&list=PLillGF-RfqbbiTGgA77tGO426V3hRF9iE&index=10).
                    This process involved making changes to my backend to ensure
                    that certain API calls required a valid token, such that the
                    database could not be arbitrarily modified by any API
                    request and instead could only be modified provided a
                    successful login.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="my-5">
            <h1>Lessons Learnt</h1>
            <p>
              At this point, I am pretty happy with the website and do not plan
              to add any additional features. That being said, I do recognize
              that it is far from perfect and many of the ways that I have
              implemented things could be improved. Here are some of things that
              I would try to do in the future:
            </p>
            <ul className="list-disc p-5">
              <li>
                Before beginning coding, plan out the entire project as much as
                possible. In particular, make sure that the frontend is written
                with a clear understanding of where API calls will need to be
                made, what those API calls will return, and how to manage state
                accordingly.
              </li>
              <li>
                Don't be afraid to use new libraries / frameworks. Although it
                is good to understand the underlying technologies (eg: coding in
                vanilla Javascript rather than React), there is a reason why
                they are used, and that is to make things easier for you. In
                particular, I would like to try using UI libraries such as
                MaterialUI in the future.
              </li>
              <li>
                Use TypeScript as much as possible. TypeScript is your friend,
                and helps you understand your code and use it more predictably.
                There are many type declarations that are optional, such as
                function return types, that you should opt to use almost always,
                even if it is obvious.
              </li>
              <li>
                Specific to React, during planning, take better consideration as
                to whether a component should be a functional component or a
                class component. Although there isn't much difference, if a
                component has many state variables, its probably easier to have
                a single setState function rather than 10+ useState hooks.
                Alternatively, a better idea might be to use the useReduce hook.
              </li>
            </ul>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaperTradeMERN;
